import React, { useEffect, useMemo, useState } from 'react';
import Map, { Marker, Popup } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { isBooked, stageOf } from './stages';
import './RouteMap.css';

/**
 * The day's route on a map — numbered pins for booked stops (same numbers as the list),
 * grey pins for cancelled / rebook / rescheduled. Addresses are looked up with Mapbox
 * (the public map token), cached for the session.
 */

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const GTA = { longitude: -79.3832, latitude: 43.6532, zoom: 9 };
const cache = {};

const geocode = async (address) => {
  if (!address) return null;
  if (cache[address] !== undefined) return cache[address];
  try {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${TOKEN}&country=ca&limit=1`;
    const res = await fetch(url);
    const json = await res.json();
    const c = json.features && json.features[0] && json.features[0].center;
    cache[address] = c ? { longitude: c[0], latitude: c[1] } : null;
  } catch {
    cache[address] = null;
  }
  return cache[address];
};

const time = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const RouteMap = ({ appointments, onOpen }) => {
  const [points, setPoints] = useState({});
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    let live = true;
    Promise.all(appointments.map(async (a) => [a.id, await geocode(a.serviceAddress)])).then((pairs) => {
      if (live) setPoints(Object.fromEntries(pairs.filter(([, p]) => p)));
    });
    return () => {
      live = false;
    };
  }, [appointments]);

  // frame every stop that was found
  const view = useMemo(() => {
    const found = Object.values(points);
    if (!found.length) return GTA;
    const lng = found.map((p) => p.longitude);
    const lat = found.map((p) => p.latitude);
    return {
      bounds: [[Math.min(...lng) - 0.02, Math.min(...lat) - 0.02], [Math.max(...lng) + 0.02, Math.max(...lat) + 0.02]],
      fitBoundsOptions: { padding: 40, maxZoom: 13 },
    };
  }, [points]);

  if (!TOKEN) return <div className="route-map-empty">Map unavailable — the Mapbox token isn’t set.</div>;

  const pickedApt = picked && appointments.find((a) => a.id === picked);
  const ready = Object.keys(points).length > 0;

  return (
    <div className="route-map">
      <Map
        key={ready ? 'framed' : 'gta'}
        mapboxAccessToken={TOKEN}
        initialViewState={view}
        mapStyle="mapbox://styles/mapbox/dark-v11"
        style={{ width: '100%', height: '100%' }}
      >
        {appointments.map((a, idx) => {
          const p = points[a.id];
          if (!p) return null;
          const booked = isBooked(a);
          return (
            <Marker key={a.id} longitude={p.longitude} latitude={p.latitude} anchor="bottom" onClick={(e) => { e.originalEvent.stopPropagation(); setPicked(a.id); }}>
              <span className={`route-pin${booked ? '' : ' off'}`}>{booked ? idx + 1 : '–'}</span>
            </Marker>
          );
        })}

        {pickedApt && points[pickedApt.id] && (
          <Popup
            longitude={points[pickedApt.id].longitude}
            latitude={points[pickedApt.id].latitude}
            anchor="top"
            onClose={() => setPicked(null)}
            closeOnClick={false}
          >
            <div className="route-popup">
              <b>{pickedApt.contactName}</b>
              <span>{time(pickedApt.appointmentStart)} – {time(pickedApt.appointmentEnd)}</span>
              <span>{pickedApt.serviceAddress}</span>
              <span className={`route-popup-stage ${stageOf(pickedApt).tone}`}>{stageOf(pickedApt).label}</span>
              <button type="button" onClick={() => onOpen(pickedApt)}>Open lead →</button>
            </div>
          </Popup>
        )}
      </Map>
      {!ready && appointments.length > 0 && <div className="route-map-loading">Finding addresses…</div>}
      {appointments.length === 0 && <div className="route-map-loading">No appointments this day.</div>}
    </div>
  );
};

export default RouteMap;
